"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Bold,
  Italic,
  Code2,
  Quote,
  List,
  Send,
  Square,
  ChevronDown,
  Check,
  X,
  HelpCircle,
  Wand2,
  Languages,
  Mic,
  Paperclip,
  Link2,
  Sparkles,
} from "lucide-react";
import { clsx, type ClassValue } from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SlashCommand, SLASH_COMMANDS } from "@/lib/slashCommands";
import { detectGarbledInput } from "@/lib/sanitize";

/** Attachment payload sent to the backend /ask endpoint. */
export type AttachmentPayload = {
  filename?: string;
  mime?: string;
  content?: string; // base64-encoded file_bytes
  url?: string; // URL to fetch (only honoured server-side with explicit opt-in)
};

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  /** Canonical answer mode sent to the backend. */
  answerMode:
    | "normal"
    | "exam"
    | "manual"
    | "stepwise"
    | "socratic"
    | "podcast"
    | "minimal"
    | "text_image"
    | "genius";
  onAnswerModeChange: (
    mode:
      | "normal"
      | "exam"
      | "manual"
      | "stepwise"
      | "socratic"
      | "podcast"
      | "minimal"
      | "text_image"
      | "genius",
  ) => void;
  podcastMode?: boolean;
  onPodcastToggle?: () => void;
  autoFocus?: boolean;
  onStop?: () => void;
  replyTo?: { id: string; content: string } | null;
  onClearReply?: () => void;
  attachment?: AttachmentPayload | null;
  onAttachmentChange: (attachment: AttachmentPayload | null) => void;
  useWebContext?: boolean;
  onUseWebContextChange?: (value: boolean) => void;
}

type ModelOption = {
  id:
    | "normal"
    | "exam"
    | "manual"
    | "stepwise"
    | "socratic"
    | "podcast"
    | "minimal"
    | "text_image"
    | "genius";
  label: string;
  sub: string;
};

const MODELS: ModelOption[] = [
  { id: "normal", label: "Standard", sub: "standard 5-section answer" },
  { id: "exam", label: "Exam", sub: "brief + detailed" },
  { id: "manual", label: "Manual", sub: "literal step-by-step" },
  { id: "stepwise", label: "Stepwise", sub: "guided walkthrough" },
  { id: "socratic", label: "Socratic", sub: "with web + media" },
  { id: "text_image", label: "Text + Image", sub: "visual answer" },
  { id: "minimal", label: "Minimal", sub: "fast text-only" },
  { id: "genius", label: "Genius", sub: "practice + exam generator" },
  { id: "podcast", label: "Podcast", sub: "audio only" },
];

const modeLabel = (id: string) => MODELS.find((m) => m.id === id)?.label || id;

export function ChatInput({
  value,
  onChange,
  onSend,
  disabled,
  answerMode,
  onAnswerModeChange,
  podcastMode = false,
  onPodcastToggle,
  autoFocus,
  onStop,
  replyTo,
  onClearReply,
  attachment = null,
  onAttachmentChange,
  useWebContext = false,
  onUseWebContextChange,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showChars, setShowChars] = useState(false);
  const [focused, setFocused] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashSuppressed, setSlashSuppressed] = useState(false);
  const recognitionRef = useRef<any>(null);

  const maxChars = 2000;
  const isTooLong = value.length > maxChars;
  const nearLimit = value.length > maxChars * 0.9;
  const canSend = value.trim().length > 0 || !!attachment;

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Auto-grow textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [value]);

  // URL auto-detection — when_the_user_pastes_a_bare_URL, automatically
  // enable web context so the AI fetches and reasons over the page content.
  const bareUrlRegex = /^https?:\/\/[^\s]+$/;
  const isBareUrl = bareUrlRegex.test(value.trim());

  // Slash command detection. Match a "/" command at start of input or after
  // whitespace, capturing the (possibly partial) command name so the menu can
  // be filtered to what the user is actually typing.
  const slashCaret =
    typeof document !== "undefined" && textareaRef.current
      ? textareaRef.current.selectionStart
      : value.length;
  const slashMatch = /(^|\s)\/(\w*)$/.exec(value.slice(0, slashCaret));
  const slashQuery = slashMatch ? slashMatch[2].toLowerCase() : "";
  const slashFiltered = slashMatch
    ? SLASH_COMMANDS.filter((c) =>
        c.cmd.slice(1).toLowerCase().startsWith(slashQuery),
      )
    : [];
  const slashOpen = slashFiltered.length > 0 && !slashSuppressed;

  // Reset slash-menu highlight/suppression whenever the typed command changes.
  useEffect(() => {
    setSlashIndex(0);
    if (!slashMatch) setSlashSuppressed(false);
  }, [slashQuery]);

  const handleSend = useCallback(() => {
    if (disabled) {
      onStop?.();
      return;
    }
    const text = value.trim();
    if (!text && !attachment) {
      toast.error("Please enter a question to continue.");
      return;
    }
    if (text.length > maxChars) {
      toast.error(
        `Input exceeds ${maxChars} characters — please shorten your question.`,
      );
      return;
    }
    // Detect garbled / non-meaningful input before sending to the backend.
    if (text && !attachment) {
      const garbageReason = detectGarbledInput(text);
      if (garbageReason) {
        toast.error(garbageReason);
        return;
      }
    }
    // When_the_user_sends_a_bare_URL, auto-enable web context so the AI
    // fetches and reasons over the page content.
    if (isBareUrl) {
      onUseWebContextChange?.(true);
    }
    onSend();
  }, [
    value,
    disabled,
    onSend,
    onStop,
    attachment,
    isBareUrl,
    onUseWebContextChange,
  ]);

  const handleFilePicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // reset so picking the same file again re-triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          // data: URLs arrive as "data:mime;base64,...." — strip the prefix.
          const comma = result.indexOf(",");
          const content = comma >= 0 ? result.slice(comma + 1) : result;
          onAttachmentChange({
            filename: file.name,
            mime: file.type || undefined,
            content,
          });
        }
      };
      reader.onerror = () => {
        // Fallback: read as text if FileReader fails for any reason.
        file.text().then((t) =>
          onAttachmentChange({
            filename: file.name,
            mime: file.type || "text/plain",
            content: btoa(unescape(encodeURIComponent(t))),
          }),
        );
      };
      reader.readAsDataURL(file);
    },
    [onAttachmentChange],
  );

  const wrapSelection = useCallback(
    (pre: string, post: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const val = textarea.value;
      textarea.value =
        val.slice(0, start) +
        pre +
        val.slice(start, end) +
        post +
        val.slice(end);
      textarea.focus();
      textarea.selectionStart = start + pre.length;
      textarea.selectionEnd = end + pre.length;
      onChange(textarea.value);
    },
    [onChange],
  );

  const handleSlashSelect = useCallback(
    (item: SlashCommand) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const caret = textarea.selectionStart ?? value.length;
      const before = textarea.value.slice(0, caret);
      const m = /(^|\s)\/(\w*)$/.exec(before);
      if (!m) return;
      // Keep any whitespace that preceded the slash so words don't glue together.
      const lead = before.slice(0, m.index + m[1].length);
      const tail = textarea.value.slice(caret);
      if (item.mode) {
        // Mode command: set the active answer mode and remove the slash token
        // from the visible input (it is not sent to the API).
        onAnswerModeChange(item.mode);
        textarea.value = lead + tail;
      } else if (item.prompt) {
        // Prompt command: inject the actual instruction prefix (item.prompt),
        // NOT the bare slash token (item.cmd), so the backend receives the
        // real guidance instead of e.g. "/summarize ".
        textarea.value = lead + item.prompt + tail;
      } else {
        return;
      }
      onChange(textarea.value);
      textarea.focus();
    },
    [onAnswerModeChange, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (slashOpen && slashFiltered.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSlashIndex((i) => (i + 1) % slashFiltered.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSlashIndex(
            (i) => (i - 1 + slashFiltered.length) % slashFiltered.length,
          );
          return;
        }
        if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
          e.preventDefault();
          handleSlashSelect(slashFiltered[slashIndex]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setSlashSuppressed(true);
          return;
        }
      }
      if (e.key === "Enter" && !e.shiftKey && !slashOpen) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === "Escape") {
        setModelOpen(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        wrapSelection("**", "**");
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "i") {
        e.preventDefault();
        wrapSelection("_", "_");
      }
    },
    [
      handleSend,
      slashOpen,
      slashFiltered,
      slashIndex,
      handleSlashSelect,
      wrapSelection,
    ],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Voice input — client-side Web Speech API. Appends the recognised transcript
  // to the composer. Toggles off if already recording.
  const startVoiceInput = useCallback(() => {
    if (typeof window === "undefined") return;
    const SR: any =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input is not supported in this browser.");
      return;
    }
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const recognition = new SR();
    recognition.lang = "en-NG";
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onresult = (event: any) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        const sep =
          value && !value.endsWith(" ") && !value.endsWith("\n") ? " " : "";
        onChange(value + sep + transcript.trim());
      }
    };
    recognition.onerror = (event: any) => {
      if (event?.error !== "no-speech" && event?.error !== "aborted") {
        toast.error("Voice input failed. Please try again.");
      }
    };
    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsRecording(true);
    } catch {
      setIsRecording(false);
      recognitionRef.current = null;
    }
  }, [isRecording, value, onChange]);

  // Stop any in-flight recognition when the component unmounts.
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* no-op */
      }
    };
  }, []);

  // Token ring progress
  const pct = Math.min(value.length / maxChars, 1);
  const circumference = 66;
  const offset = circumference - circumference * pct;

  const handleModelSelect = useCallback(
    (modelId: ChatInputProps["answerMode"]) => {
      onAnswerModeChange(modelId);
      setModelOpen(false);
    },
    [onAnswerModeChange],
  );

  return (
    <div className="flex flex-col gap-1.5 w-full max-w-[720px] mx-auto">
      {/* Reply tag */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-center gap-2 rounded-lg border border-border/30 bg-y/5 px-2.5 py-1.5 text-xs"
          >
            <span className="text-xs font-bold text-y">↳ Replying to:</span>
            <span className="truncate text-foreground/80">
              {replyTo.content.slice(0, 120)}
              {replyTo.content.length > 120 ? "…" : ""}
            </span>
            {onClearReply && (
              <button
                type="button"
                onClick={onClearReply}
                className="ml-auto rounded-full p-0.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                title="Clear reply"
              >
                <X size={10} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toolbar strip - hidden on mobile, shown on focus */}
      <motion.div
        initial={false}
        animate={{ opacity: focused ? 1 : 0, y: focused ? 0 : 4 }}
        transition={{ duration: 0.15 }}
        className={cn(
          "hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg border border-border/30 bg-card/80 backdrop-blur-md",
          !focused && "pointer-events-none",
        )}
      >
        <button
          type="button"
          onClick={() => wrapSelection("**", "**")}
          className="min-w-[32px] min-h-[32px] rounded-md flex items-center justify-center text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          title="Bold (Ctrl+B)"
        >
          <Bold size={12} />
        </button>
        <button
          type="button"
          onClick={() => wrapSelection("_", "_")}
          className="min-w-[32px] min-h-[32px] rounded-md flex items-center justify-center text-xs italic text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          title="Italic (Ctrl+I)"
        >
          <Italic size={12} />
        </button>
        <button
          type="button"
          onClick={() => wrapSelection("`", "`")}
          className="min-w-[32px] min-h-[32px] rounded-md flex items-center justify-center text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          title="Code"
        >
          <Code2 size={12} />
        </button>
        <div className="w-px h-3 bg-border/50 mx-0.5" />
        <button
          type="button"
          onClick={() => wrapSelection("\n> ", "")}
          className="min-w-[32px] min-h-[32px] rounded-md flex items-center justify-center text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          title="Quote"
        >
          <Quote size={12} />
        </button>
        <button
          type="button"
          onClick={() => wrapSelection("\n- ", "")}
          className="min-w-[32px] min-h-[32px] rounded-md flex items-center justify-center text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          title="Bulleted list"
        >
          <List size={12} />
        </button>
      </motion.div>

      {/* Attachment chip */}
      <AnimatePresence>
        {attachment && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-center gap-2 rounded-lg border border-y/30 bg-y/5 px-2.5 py-1.5 text-xs mb-1"
          >
            <Paperclip size={11} className="text-y" />
            {attachment.url !== undefined ? (
              <input
                type="url"
                value={attachment.url}
                onChange={(e) => onAttachmentChange({ url: e.target.value })}
                placeholder="Paste a URL to use as context"
                className="flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground/40 text-xs"
              />
            ) : (
              <span className="truncate text-foreground/80 text-xs">
                {attachment.filename || "attachment"}
              </span>
            )}
            {attachment.url !== undefined && (
              <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={useWebContext}
                  onChange={(e) => onUseWebContextChange?.(e.target.checked)}
                />
                Use web context
              </label>
            )}
            <button
              type="button"
              onClick={() => onAttachmentChange(null)}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              title="Remove attachment"
            >
              <X size={10} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active answer-mode pill */}
      {answerMode !== "normal" && (
        <div className="flex items-center justify-end mb-1">
          <button
            type="button"
            onClick={() => onAnswerModeChange("normal")}
            title="Click to return to standard mode"
            className="flex items-center gap-1 rounded-full border border-y/40 bg-y px-2 py-0.5 font-syne text-[9px] font-bold uppercase tracking-wider text-black hover:bg-y-light transition-colors"
          >
            {modeLabel(answerMode)} Mode
            <X size={9} className="opacity-70" />
          </button>
        </div>
      )}

      {/* Composer */}
      <div
        ref={composerRef}
        className={cn(
          "relative border border-border/50 bg-card/60 backdrop-blur-xl rounded-xl px-2 py-1.5 sm:px-2.5 sm:py-1.5 transition-all duration-200",
          focused &&
            "border-y/40 shadow-[0_0_0_2px_rgba(255,214,0,0.08),0_0_16px_rgba(255,214,0,0.03)]",
        )}
      >
        {/* Input row with float menus */}
        <div className="relative">
          {/* Slash command menu */}
          <AnimatePresence>
            {slashOpen && (
              <motion.div
                id="slash-menu"
                role="listbox"
                aria-label="Slash commands"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.12 }}
                className="absolute bottom-full left-0 w-[260px] max-w-[calc(100vw-1.5rem)] mb-2 flex flex-col bg-card border border-border/50 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-1 z-50"
              >
                <div className="px-2 py-1 text-[9px] font-mono text-muted-foreground uppercase tracking-wider">
                  Commands
                </div>
                {slashFiltered.map((sc, i) => (
                  <button
                    key={sc.cmd}
                    id={`slash-opt-${i}`}
                    role="option"
                    aria-selected={i === slashIndex}
                    type="button"
                    tabIndex={-1}
                    onMouseEnter={() => setSlashIndex(i)}
                    onClick={() => handleSlashSelect(sc)}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-[11px] text-left transition-colors",
                      i === slashIndex
                        ? "bg-muted/40 text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                    )}
                  >
                    <span className="w-5 h-5 rounded-md bg-muted/50 flex items-center justify-center text-y flex-shrink-0 text-xs">
                      {sc.icon}
                    </span>
                    <div>
                      <div className="font-mono text-[10px]">{sc.cmd}</div>
                      <div className="text-[9px] text-muted-foreground/60">
                        {sc.label}
                      </div>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Message… try / for commands"
            disabled={disabled}
            role="combobox"
            aria-expanded={slashOpen}
            aria-controls={slashOpen ? "slash-menu" : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              slashOpen ? `slash-opt-${slashIndex}` : undefined
            }
            // 16px on small screens: iOS Safari zooms the whole page when a
            // focused field's computed font-size is under 16px, which is what
            // made the layout magnify on phones. Desktop keeps the tighter
            // 14px since the zoom only applies on touch devices.
            className="w-full bg-transparent border-none outline-none resize-none text-[16px] sm:text-[14px] leading-relaxed min-h-[28px] sm:min-h-[36px] max-h-[80px] sm:max-h-[120px] placeholder:text-muted-foreground/40 text-foreground py-1 font-syne"
            rows={2}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              // Delay to allow click events on dropdowns
              setTimeout(() => {
                if (document.activeElement !== textareaRef.current) {
                  setFocused(false);
                }
              }, 120);
            }}
            onMouseEnter={() => setShowChars(true)}
            onMouseLeave={() => !value && setShowChars(false)}
          />
        </div>

        {/* Bottom row */}
        <div className="flex items-center justify-between mt-1.5 gap-2">
          <div className="flex items-center gap-1">
            {/* Model picker */}
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setModelOpen(!modelOpen);
                }}
                className="flex items-center gap-1 bg-muted/30 border border-border/30 rounded-md px-2 py-1.5 cursor-pointer hover:border-y/30 transition-colors min-h-[36px]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="font-mono text-[10px] text-muted-foreground">
                  {modeLabel(answerMode)}
                </span>
                <ChevronDown size={9} className="text-muted-foreground" />
              </button>

              <AnimatePresence>
                {modelOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.12 }}
                    className="absolute bottom-full left-0 mb-2 w-[280px] max-w-[calc(100vw-2rem)] flex flex-col bg-card border border-border/50 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-1 z-50"
                  >
                    {MODELS.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => handleModelSelect(m.id)}
                        className={cn(
                          "flex items-center justify-between w-full px-2 py-1.5 rounded-md text-[11px] font-mono text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors text-left",
                          answerMode === m.id && "text-y",
                        )}
                      >
                        <span className="truncate">
                          {m.label} · {m.sub}
                        </span>
                        <span
                          className={cn(
                            "opacity-0",
                            answerMode === m.id && "opacity-100",
                          )}
                        >
                          <Check size={11} />
                        </span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Podcast mode toggle */}
            {onPodcastToggle && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPodcastToggle?.();
                  onAnswerModeChange(
                    answerMode === "podcast" ? "normal" : "podcast",
                  );
                }}
                className={cn(
                  "w-8 h-8 rounded-md flex items-center justify-center text-xs transition-colors border",
                  podcastMode || answerMode === "podcast"
                    ? "bg-y/20 text-y border-y/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border-transparent",
                )}
                title="Podcast mode (audio-only answer)"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 10.5c0 2.21-1.79 4-4 4s-4-1.79-4-4H7c0 2.53 1.97 4.59 4.5 4.91V17c0 .55.45 1 1 1h3c.55 0 1-.45 1-1v-2.9c2.53-.32 4.5-2.38 4.5-4.91h-1z" />
                </svg>
              </button>
            )}

            {/* Attach file */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center text-xs transition-colors border",
                attachment && !attachment.url
                  ? "bg-y/20 text-y border-y/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border-transparent",
              )}
              title="Attach a file"
            >
              <Paperclip size={12} />
            </button>

            {/* Attach URL */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (attachment?.url !== undefined) {
                  onAttachmentChange(null);
                } else {
                  onAttachmentChange({ url: "" });
                }
              }}
              className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center text-xs transition-colors border",
                attachment?.url !== undefined
                  ? "bg-y/20 text-y border-y/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border-transparent",
              )}
              title="Attach a URL"
            >
              <Link2 size={12} />
            </button>

            {/* Voice input */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                startVoiceInput();
              }}
              disabled={disabled}
              className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center text-xs transition-colors border relative disabled:opacity-40 disabled:cursor-not-allowed",
                isRecording
                  ? "bg-red-500/20 text-red-400 border-red-400/40"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border-transparent",
              )}
              title={isRecording ? "Stop voice input" : "Voice input"}
              aria-label={isRecording ? "Stop voice input" : "Voice input"}
            >
              <Mic size={12} />
              {isRecording && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFilePicked}
            />
          </div>

          {/* Right_side: token ring + send */}
          <div className="flex items-center gap-2">
            {/* Token ring */}
            <div
              className="relative w-[22px] h-[22px]"
              title={`Context used: ${Math.round(pct * 100)}%`}
            >
              <svg
                className="w-[22px] h-[22px]"
                viewBox="0 0 22 22"
                style={{ transform: "rotate(-90deg)" }}
              >
                <circle
                  className="stroke-border/30"
                  cx="11"
                  cy="11"
                  r="8.5"
                  fill="none"
                  strokeWidth="2.5"
                />
                <circle
                  className={cn(
                    "fill-none stroke-y transition-all duration-150",
                    pct > 0.9 && "stroke-danger-bright",
                  )}
                  cx="11"
                  cy="11"
                  r="8.5"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center font-mono text-[7px] text-muted-foreground">
                {Math.round(pct * 100)}%
              </div>
            </div>

            {/* Send / Stop button */}
            <button
              type="button"
              onClick={handleSend}
              disabled={!disabled && (!canSend || isTooLong)}
              className={cn(
                "w-8 h-8 rounded-lg border-none cursor-pointer flex items-center justify-center transition-all shrink-0",
                disabled
                  ? "bg-gradient-to-br from-danger-bright to-danger text-white shadow-[0_4px_16px_rgba(217,86,78,0.25)]"
                  : value.trim() && !isTooLong
                    ? "bg-gradient-to-br from-y to-y-light text-black shadow-[0_4px_16px_rgba(255,214,0,0.25)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(255,214,0,0.35)] active:translate-y-0 active:scale-95"
                    : "bg-muted/40 text-muted-foreground/40 cursor-not-allowed opacity-50",
              )}
              aria-label={disabled ? "Stop generating" : "Send message"}
            >
              {disabled ? (
                <Square size={13} className="fill-current" />
              ) : (
                <Send size={13} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Hint row - compact */}
      <div className="flex items-center justify-between mt-1 px-0.5">
        <span className="font-mono text-[9px] text-muted-foreground/50 tracking-wide">
          ENTER TO SEND · SHIFT+ENTER NEWLINE · / COMMANDS
        </span>
        <span
          className={cn(
            "font-mono text-[9px] text-muted-foreground/50 transition-colors",
            nearLimit && "text-danger-bright",
          )}
        >
          {value.length} / {maxChars}
        </span>
      </div>
    </div>
  );
}
